import { useEffect, useState } from "react";
import {
  deleteAdminOrder,
  fetchAdminOrders,
  updateAdminOrderStatus,
} from "../api";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { id: "new", label: "Új" },
  { id: "preparing", label: "Folyamatban" },
  { id: "fulfilled", label: "Teljesítve" },
  { id: "cancelled", label: "Lemondva" },
];

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("hu-HU", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function formatPrice(huf) {
  const n = Number(huf) || 0;
  return `${n}.-`;
}

function IconRefresh() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
    </svg>
  );
}

function OrderRow({
  order,
  isOpen,
  saving,
  deleting,
  onToggle,
  onStatusChange,
  onDelete,
}) {
  return (
    <>
      <tr className={isOpen ? "is-expanded" : undefined} onClick={onToggle}>
        <td>{formatDate(order.paidAt)}</td>
        <td>
          {order.customerName || "—"}
          {order.orderType === "cart" ? (
            <span className="admin-orders-type">Webshop</span>
          ) : null}
        </td>
        <td>{order.customerEmail || "—"}</td>
        <td>{order.shippingMethodLabel || order.shippingMethod || "—"}</td>
        <td>{formatPrice(order.amountTotalHuf)}</td>
        <td onClick={(event) => event.stopPropagation()}>
          <select
            className={`admin-orders-status admin-orders-status--${order.status}`}
            value={order.status}
            disabled={saving || deleting}
            onChange={(event) => onStatusChange(order.id, event.target.value)}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </td>
        <td onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            className="admin-orders-delete"
            disabled={saving || deleting}
            onClick={() => onDelete(order)}
          >
            {deleting ? "…" : "Törlés"}
          </button>
        </td>
      </tr>
      {isOpen ? (
        <tr className="admin-orders-detail-row">
          <td colSpan={7}>
            <div className="admin-orders-detail">
              <p>
                <strong>Telefon:</strong> {order.customerPhone || "—"}
              </p>
              <p>
                <strong>Csomagpont / cím:</strong>{" "}
                {order.pickupPoint || order.shippingAddress || "—"}
              </p>
              {order.billingAddress ? (
                <p>
                  <strong>Számlázás:</strong> {order.billingAddress}
                </p>
              ) : null}
              {order.orderType === "cart" ? (
                <div className="admin-orders-items">
                  <strong>Tételek (webshop):</strong>
                  <ul>
                    {order.items.map((item) => (
                      <li key={item.productId}>
                        {item.quantity} × {item.name}
                        {item.unitLabel ? ` (${item.unitLabel})` : ""} —{" "}
                        {formatPrice(item.lineTotalHuf)}
                      </li>
                    ))}
                  </ul>
                  <p>
                    <strong>Részösszeg:</strong> {formatPrice(order.subtotalHuf)}
                    {" · "}
                    <strong>Szállítás:</strong> {formatPrice(order.shippingFeeHuf)}
                  </p>
                </div>
              ) : (
                <p>
                  <strong>Kávé:</strong> {formatPrice(order.coffeePriceHuf)}
                  {" · "}
                  <strong>Szállítás:</strong> {formatPrice(order.shippingFeeHuf)}
                </p>
              )}
              <p>
                <strong>Stripe ID:</strong> {order.paymentIntentId || "—"}
              </p>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AdminOrders({ token }) {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(0);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    async function load(silent = false) {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError("");

      try {
        const data = await fetchAdminOrders(token, {
          status: statusFilter,
          q: debouncedQuery,
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
        });
        if (cancelled) return;
        setOrders(data.orders || []);
        setTotal(data.total || 0);
      } catch {
        if (!cancelled) setError("Nem sikerült betölteni a rendeléseket.");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    }

    load(false);
    const interval = window.setInterval(() => load(true), 30000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [token, statusFilter, debouncedQuery, page]);

  async function handleRefresh() {
    if (!token || refreshing) return;
    setRefreshing(true);
    setError("");
    try {
      const data = await fetchAdminOrders(token, {
        status: statusFilter,
        q: debouncedQuery,
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
      });
      setOrders(data.orders || []);
      setTotal(data.total || 0);
      setMessage("Frissítve.");
    } catch {
      setError("A frissítés sikertelen.");
    } finally {
      setRefreshing(false);
    }
  }

  async function handleStatusChange(orderId, status) {
    if (!token || savingId || deletingId) return;
    setSavingId(orderId);
    setError("");
    setMessage("");
    try {
      const data = await updateAdminOrderStatus(token, orderId, status);
      setOrders((current) =>
        current.map((order) => (order.id === orderId ? data.order : order)),
      );
      setMessage("Státusz mentve.");
    } catch {
      setError("A státusz mentése sikertelen.");
    } finally {
      setSavingId(null);
    }
  }

  async function handleDelete(order) {
    if (!token || savingId || deletingId) return;
    const label = order.customerName || order.customerEmail || "ezt a rendelést";
    if (!window.confirm(`Biztosan törlöd a rendelést (${label})?`)) return;

    setDeletingId(order.id);
    setError("");
    setMessage("");
    try {
      await deleteAdminOrder(token, order.id);
      setOrders((current) => current.filter((entry) => entry.id !== order.id));
      setTotal((current) => Math.max(0, current - 1));
      if (expandedId === order.id) setExpandedId(null);
      setMessage("Rendelés törölve.");
    } catch {
      setError("A törlés sikertelen.");
    } finally {
      setDeletingId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <header className="admin-dash-header">
        <div>
          <h1 className="admin-dash-title">Rendelések</h1>
          <p className="admin-dash-subtitle">
            Kávérendelések követése és státuszkezelés.
          </p>
        </div>
      </header>

      <section className="admin-dash-toolbar">
        <label className="admin-dash-search">
          <span className="visually-hidden">Keresés</span>
          <input
            type="search"
            placeholder="Név, e-mail, telefon…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="admin-dash-toolbar-actions">
          <label className="admin-orders-filter">
            <span className="visually-hidden">Státusz</span>
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(0);
              }}
            >
              <option value="">Minden státusz</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className={`admin-dash-btn admin-dash-btn-outline admin-dash-btn-icon${refreshing ? " is-spinning" : ""}`}
            onClick={handleRefresh}
            disabled={refreshing || loading}
            aria-label="Frissítés"
            title="Frissítés"
          >
            <IconRefresh />
          </button>
        </div>
      </section>

      {error ? (
        <p className="newsletter-modal-error" role="alert">
          {error}
        </p>
      ) : null}
      {message ? <p className="admin-dash-toast">{message}</p> : null}

      <section className="admin-dash-table-card">
        {loading ? (
          <p className="admin-dash-empty">Betöltés…</p>
        ) : orders.length === 0 ? (
          <p className="admin-dash-empty">
            Még nincs rendelés. Az új fizetések itt jelennek meg.
          </p>
        ) : (
          <>
            <div className="admin-dash-table-wrap">
              <table className="admin-dash-table admin-orders-table">
                <thead>
                  <tr>
                    <th>Dátum</th>
                    <th>Név</th>
                    <th>E-mail</th>
                    <th>Szállítás</th>
                    <th>Összeg</th>
                    <th>Státusz</th>
                    <th aria-label="Műveletek" />
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const isOpen = expandedId === order.id;
                    return (
                      <OrderRow
                        key={order.id}
                        order={order}
                        isOpen={isOpen}
                        saving={savingId === order.id}
                        deleting={deletingId === order.id}
                        onToggle={() => setExpandedId(isOpen ? null : order.id)}
                        onStatusChange={handleStatusChange}
                        onDelete={handleDelete}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>

            <footer className="admin-dash-table-foot">
              <p>
                {total === 0
                  ? "0 rendelés"
                  : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} / ${total} rendelés`}
              </p>
              <div className="admin-dash-pagination">
                <button
                  type="button"
                  className="admin-dash-page-btn"
                  disabled={page <= 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                  aria-label="Előző oldal"
                >
                  ‹
                </button>
                <span>
                  {page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  className="admin-dash-page-btn"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setPage((current) => current + 1)}
                  aria-label="Következő oldal"
                >
                  ›
                </button>
              </div>
            </footer>
          </>
        )}
      </section>
    </>
  );
}

export default AdminOrders;
